/**
 * GoTrue client — email one-time codes.
 *
 * Both roles sign in the same way: type an email, receive a 6-digit code, type
 * it back. The spec originally gave clients Google sign-in; that is now an
 * optional convenience on top of this rather than a dependency, because it
 * needed credentials that were not available and OTP works for everyone.
 *
 * Deliberately dependency-free (plain fetch): the same file is used by two web
 * apps and two React Native apps, and @supabase/supabase-js would pull a
 * realtime client and a postgrest client that none of them use.
 */
export class AuthError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'AuthError';
    }
}
/** GoTrue is inconsistent about which field carries the message; try them all. */
function toAuthError(status, body) {
    const b = (body ?? {});
    const raw = b.error_description ?? b.msg ?? b.message ?? b.error ?? 'Authentication failed.';
    const code = b.error_code ?? b.error ?? String(status);
    // Turn the ones people actually hit into something worth reading.
    const friendly = status === 429
        ? 'Too many attempts. Wait a minute and try again.'
        : /expired/i.test(raw)
            ? 'That code has expired. Ask for a new one.'
            : /invalid|not found/i.test(raw) && /token|otp|code/i.test(raw)
                ? 'That code is not right. Check the email and try again.'
                : raw;
    return new AuthError(status, code, friendly);
}
async function call(baseUrl, path, body) {
    let res;
    try {
        res = await fetch(`${baseUrl}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    }
    catch {
        throw new AuthError(0, 'OFFLINE', 'Cannot reach the sign-in service. Check your connection.');
    }
    const text = await res.text();
    let payload = null;
    try {
        payload = text ? JSON.parse(text) : null;
    }
    catch {
        payload = text;
    }
    if (!res.ok)
        throw toAuthError(res.status, payload);
    return payload;
}
/**
 * Send a 6-digit code to an email address.
 *
 * `create_user: true` means a first-time visitor is signed up by the same
 * action — there is no separate registration step, which is the whole point of
 * an OTP flow.
 *
 * Returns nothing on purpose: the response is identical whether or not the
 * address is already registered, so this cannot be used to enumerate accounts.
 */
export async function requestOtp(baseUrl, email) {
    await call(baseUrl, '/otp', { email: email.trim().toLowerCase(), create_user: true });
}
/**
 * The seeded test accounts can be signed in with a fixed code, so testing
 * three apps does not mean fetching a code off the server every time.
 *
 * GoTrue is asked FIRST and always. This runs only after a real code has been
 * refused, so it can never shadow a genuine sign-in, and the server refuses
 * everything unless TEST_SIGNIN_CODE is set and the address is one of the
 * seven seeded fixtures. On a server with the flag unset -- which is the
 * normal state -- this is one wasted request and the original error.
 */
async function testSignIn(authBaseUrl, email, code) {
    // Derived from the auth URL, never hardcoded relative. The published apps
    // point at https://api.hundai.tech/auth/v1, and the web vhosts answer every
    // unknown path with index.html -- a relative '/v1' would fetch the SPA and
    // then fail parsing HTML as JSON.
    const api = authBaseUrl.replace(/\/auth\/v1\/?$/, '/v1');
    try {
        const res = await fetch(`${api}/auth/test-signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code }),
        });
        if (!res.ok)
            return null;
        const raw = (await res.json());
        if (!raw.access_token)
            return null;
        return {
            access_token: raw.access_token,
            refresh_token: raw.refresh_token,
            expires_at: Math.floor(Date.now() / 1000) + (raw.expires_in ?? 3600),
            user: raw.user,
        };
    }
    catch {
        return null;
    }
}
/** Exchange the emailed code for a session. */
export async function verifyOtp(baseUrl, email, token) {
    const address = email.trim().toLowerCase();
    const code = token.trim();
    let raw;
    try {
        raw = await call(baseUrl, '/verify', {
            email: address,
            token: code,
            type: 'email',
        });
    }
    catch (err) {
        const test = await testSignIn(baseUrl, address, code);
        if (test)
            return test;
        throw err;
    }
    return {
        access_token: raw.access_token,
        refresh_token: raw.refresh_token,
        expires_at: Math.floor(Date.now() / 1000) + (raw.expires_in ?? 3600),
        user: raw.user,
    };
}
/** Trade a refresh token for a fresh session. */
export async function refreshSession(baseUrl, refreshToken) {
    const raw = await call(baseUrl, '/token?grant_type=refresh_token', { refresh_token: refreshToken });
    return {
        access_token: raw.access_token,
        refresh_token: raw.refresh_token,
        expires_at: Math.floor(Date.now() / 1000) + (raw.expires_in ?? 3600),
        user: raw.user,
    };
}
export async function signOut(baseUrl, accessToken) {
    // Best effort: the local session is cleared regardless, so a failure here
    // must never leave someone stuck on a screen they are trying to leave.
    try {
        await fetch(`${baseUrl}/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }
    catch { /* ignore */ }
}
/** True when the session is gone or within 60s of expiring. */
export function needsRefresh(session) {
    if (!session)
        return true;
    return session.expires_at - 60 <= Math.floor(Date.now() / 1000);
}
/** The Google sign-in URL, for when OAuth credentials eventually exist. */
export function googleSignInUrl(baseUrl, redirectTo) {
    return `${baseUrl}/authorize?provider=google&redirect_to=${encodeURIComponent(redirectTo)}`;
}
//# sourceMappingURL=gotrue.js.map